import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  useToast,
  Flex,
  Spinner,
  Text,
  InputGroup,
  Input,
  InputRightElement,
  IconButton,
  Select,
  HStack,
  Button,
  ButtonGroup,
  Image,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, ArrowBackIcon, ViewIcon, ChevronUpIcon } from '@chakra-ui/icons';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import TasksSingleRow from '../components/tasks/TasksSingleRow';
import TasksGroupedRow from '../components/tasks/TasksGroupedRow';
import TasksFilter from '../components/tasks/TasksFilter';
import { groupTasksByLead } from '../components/tasks/TasksUtils';
import NewTaskModal from '../components/tasks/NewTaskModal';

const TasksPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for new task modal
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [newTaskLeadId, setNewTaskLeadId] = useState(null);
  const [newTaskLeadDetails, setNewTaskLeadDetails] = useState(null);
  
  // Use useRef for any DOM element references
  // Initialize ref for table scrolling
  const tableContainerRef = useRef(null);
  
  // Use state to track if component is mounted
  const [isMounted, setIsMounted] = useState(false);
  
  // Filters - initialize with a status filter to exclude completed tasks
  const [filters, setFilters] = useState({
    status: 'pending',  // Only show pending tasks by default
    priority: '',
    search: '',
    assignedTo: '',
    sortField: 'dueDate',
    sortOrder: 'asc',
  });
  
  const fetchTasks = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Build query params from filters
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        ...filters
      });
      
      // Remove empty filter values
      Object.entries(filters).forEach(([key, value]) => {
        if (!value) params.delete(key);
      });
      
      const response = await api.get(`/api/tasks?${params.toString()}`);
      
      if (response.data) {
        setTasks(response.data.tasks || []);
        setTotal(response.data.total || 0);
        setPages(response.data.pages || 1);
        setCurrentPage(response.data.currentPage || 1);
      }
    } catch (error) {
      console.error('[TasksPage] Error fetching tasks:', error);
      setError('Failed to fetch tasks. Please try again.');
      toast({
        title: 'Error fetching tasks',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Effect to mark component as mounted/unmounted
  useEffect(() => {
    setIsMounted(true);
    return () => {
      // Safely nullify the ref during unmount
      tableContainerRef.current = null;
      setIsMounted(false);
    };
  }, []);
  
  // Effect to fetch tasks
  useEffect(() => {
    if (isMounted) {
      fetchTasks();
    }
  }, [currentPage, pageSize, filters.sortField, filters.sortOrder, isMounted]);
  
  const handleSearch = (evt) => {
    if (evt.key === 'Enter') {
      setCurrentPage(1); // Reset to first page on new search
      fetchTasks();
    }
  };
  
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page on filter change
    
    // Safely reset scroll position with proper checks
    if (isMounted) {
      requestAnimationFrame(() => {
        try {
          if (isMounted && tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
          }
        } catch (error) {
          // Silent catch - no need to log for normal operation
        }
      });
    }
    
    fetchTasks();
  };
  
  const handleClearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      search: '',
      assignedTo: '',
      sortField: 'dueDate',
      sortOrder: 'asc',
    });
    setCurrentPage(1);
    fetchTasks();
  };
  
  /**
   * Handle task status change with additional completion data
   * @param {string} taskId - The ID of the task to update
   * @param {string} newStatus - The new status (completed/pending)
   * @param {Object} completionData - Optional data from task completion modal
   */
  const handleTaskStatusChange = async (taskId, newStatus, completionData = null, isAssigneeChange = false) => {
    try {
      // Find the task to get any additional info we might need
      const task = tasks.find(t => t._id === taskId);
      if (!task) {
        throw new Error('Task not found');
      }
      
      // Only update the status if it's not an assignee change (which has already been saved)
      if (!isAssigneeChange) {
        await api.put(`/api/tasks/${taskId}`, { status: newStatus });
      }
      
      // Handle additional data from completion modal
      if (completionData && newStatus === 'completed') {
        // Add comment if provided
        if (completionData.comment?.trim()) {
          try {
            await api.post('/api/comments', {
              leadId: task.leadId,
              content: `Task completed: ${completionData.comment}`,
              type: 'internal'
            });
            console.log('Comment added successfully');
          } catch (commentError) {
            console.error('Error adding comment:', commentError);
            toast({
              title: 'Error adding comment',
              description: commentError.response?.data?.message || 'Failed to add comment',
              status: 'error',
              duration: 5000,
            });
          }
        }
        
        // Update lead's follow-up date if provided
        if (completionData.followUpDate && task.leadId) {
          try {
            // Handle the datetime-local format properly
            const followUpDateTime = new Date(completionData.followUpDate);
            
            // Make sure to format the date exactly as expected by the API
            const isoString = followUpDateTime.toISOString();
            console.log('Updating follow-up time with:', isoString);
            
            await api.put(`/api/leads/${task.leadId}`, {
              nextFollowUpTime: isoString
            });
            console.log('Follow-up date updated successfully');
          } catch (followUpError) {
            console.error('Error updating follow-up date:', followUpError);
            toast({
              title: 'Error updating follow-up date',
              description: followUpError.response?.data?.message || 'Failed to update follow-up date',
              status: 'error',
              duration: 5000,
            });
          }
        }
        
        // Set up new task creation if requested
        if (completionData.createAnotherTask && task.leadId) {
          // Store the lead ID and details for the new task modal
          setNewTaskLeadId(task.leadId);
          setNewTaskLeadDetails(task.leadDetails || null);
          // Open the new task modal after a slight delay to avoid UI jank
          setTimeout(() => setIsNewTaskModalOpen(true), 300);
        }
      }
      
      // Refresh tasks after update
      fetchTasks();
      
      toast({
        title: 'Task updated',
        description: isAssigneeChange ? 
          'Task assignee has been updated' : 
          (newStatus === 'completed' ? 'Task marked as completed' : 'Task status has been updated'),
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('[TasksPage] Error updating task:', error);
      toast({
        title: 'Error updating task',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    }
  };

  /**
   * Handle the creation of a new task from the modal
   */
  const handleNewTaskCreated = (newTask) => {
    // Refresh tasks to show the newly created task
    fetchTasks();
    setIsNewTaskModalOpen(false);
    setNewTaskLeadId(null);
    setNewTaskLeadDetails(null);
    
    toast({
      title: 'Task created',
      description: 'New follow-up task created successfully',
      status: 'success',
      duration: 3000,
    });
  };

  const handleTaskTitleChange = async (taskId, newTitle) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { title: newTitle });
      toast({
        title: 'Task updated',
        description: 'Task title has been updated',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('[TasksPage] Error updating task title:', error);
      toast({
        title: 'Error updating task',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    }
  };
  
  return (
    <Box bg="#fef4db" minH="100vh" width="100%" overflow="hidden">
      <Flex 
        justify="space-between" 
        align="center" 
        p={4}
        bg="#fef4db"
      >
        <HStack spacing={3}>
          <Box as="a" href="https://sumaroh.com" target="_blank" rel="noopener noreferrer">
            <Image src="/logo.png" alt="Logo" height="30px" ml={3} />
          </Box>
          <Text color="#63410d" fontSize="xl" fontWeight="thin">|</Text>
          <Heading size="lg" color="#63410d" fontWeight="thin">Tasks</Heading>
        </HStack>
        
        <ButtonGroup size="sm" spacing={2}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="solid"
            colorScheme="blue"
            onClick={() => navigate('/')}
            size="sm"
          >
            Back to Leads CRM
          </Button>
          
          <Button
            leftIcon={<ChevronUpIcon />}
            variant="ghost"
            color="#7c5913"
            _hover={{ bg: '#f9e8c0' }}
            onClick={() => navigate('/analytics')}
            size="sm"
          >
            View Analytics
          </Button>
          
          <Button
            leftIcon={<ViewIcon />}
            variant="ghost"
            color="#7c5913"
            _hover={{ bg: '#f9e8c0' }}
            onClick={() => navigate('/sheets')}
            size="sm"
          >
            Lead Sync
          </Button>
        </ButtonGroup>
      </Flex>
      
      <Box width="100%" bg="white" borderTopRadius="md" mt={1} p={5}>
        <Box mb={5}>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={2}>
              <InputGroup w="300px">
                <Input
                  placeholder="Search tasks..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyDown={handleSearch}
                />
              <InputRightElement>
                <IconButton
                  icon={<SearchIcon />}
                  size="sm"
                  aria-label="Search"
                  variant="ghost"
                  onClick={fetchTasks}
                />
              </InputRightElement>
            </InputGroup>
          </HStack>
        </Flex>
        
        <TasksFilter 
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      </Box>
      
      {isLoading ? (
        <Flex justify="center" align="center" h="300px">
          <Spinner 
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Flex>
      ) : error ? (
        <Flex justify="center" align="center" h="300px">
          <Text color="red.500">{error}</Text>
        </Flex>
      ) : (
        <>
          <Heading as="h2" size="md" mb={4} color="#63410d" px={4}>Task Management</Heading>
          <Box overflowX="auto" ref={tableContainerRef} px={4}>
            <Table variant="simple" size="sm" sx={{
              // Excel-like table styling
              'th, td': {
                padding: '2px 2px',
                whiteSpace: 'nowrap',
                fontSize: 'sm',
              },
              'th': {
                fontWeight: 'medium',
                backgroundColor: 'gray.50',
                borderBottom: '2px solid',
                borderColor: 'gray.200',
              },
              'tr': {
                borderBottom: '1px solid',
                borderColor: 'gray.100',
                _hover: { bg: 'gray.50' },
              }
            }}>
              
              <Tbody>
                {tasks.length > 0 ? (() => {
                  // Group tasks by leadId
                  const taskGroups = groupTasksByLead(tasks);
                  const renderedLeadIds = new Set();
                  const rows = [];
                  
                  // Process tasks with leadId
                  Object.entries(taskGroups).forEach(([leadId, groupTasks]) => {
                    // Skip the 'noLead' category
                    if (leadId === 'noLead') return;
                    
                    // If this lead has already been rendered, skip
                    if (renderedLeadIds.has(leadId)) return;
                    
                    // Mark this lead as rendered
                    renderedLeadIds.add(leadId);
                    
                    // Decide whether to use grouped or single row
                    if (groupTasks.length > 1) {
                      // Multiple tasks for this lead - use TasksGroupedRow
                      rows.push(
                        <TasksGroupedRow
                          key={`group-${leadId}`}
                          tasks={groupTasks}
                          onStatusChange={handleTaskStatusChange}
                          onTitleChange={handleTaskTitleChange}
                        />
                      );
                    } else if (groupTasks.length === 1) {
                      // Single task - use TasksSingleRow
                      rows.push(
                        <TasksSingleRow
                          key={groupTasks[0]._id}
                          task={groupTasks[0]}
                          onStatusChange={handleTaskStatusChange}
                          onTitleChange={handleTaskTitleChange}
                        />
                      );
                    }
                  });
                  
                  // Add tasks with no leadId
                  if (taskGroups.noLead) {
                    taskGroups.noLead.forEach(task => {
                      rows.push(
                        <TasksSingleRow
                          key={task._id}
                          task={task}
                          onStatusChange={handleTaskStatusChange}
                          onTitleChange={handleTaskTitleChange}
                        />
                      );
                    });
                  }
                  
                  return rows.length > 0 ? rows : (
                    <Tr>
                      <Th colSpan={11} textAlign="center" py={10}>
                        No tasks found.
                      </Th>
                    </Tr>
                  );
                })() : (
                  <Tr>
                    <Th colSpan={11} textAlign="center" py={10}>
                      No tasks found.
                    </Th>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
          <Flex justify="space-between" align="center" mt={4} px={4}>
            <HStack>
              <Text fontSize="sm">
                Showing {tasks.length} of {total} tasks
              </Text>
              <Select
                size="sm"
                w="100px"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </HStack>
            
            <HStack>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
                aria-label="Previous page"
              />
              <Text>Page {currentPage} of {pages}</Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pages))}
                isDisabled={currentPage === pages}
                aria-label="Next page"
              />
            </HStack>
          </Flex>
        </>
      )}
      </Box>
      
      {/* Modal for creating a new follow-up task */}
      <NewTaskModal
        isOpen={isNewTaskModalOpen}
        onClose={() => setIsNewTaskModalOpen(false)}
        leadId={newTaskLeadId}
        leadDetails={newTaskLeadDetails}
        onTaskCreated={handleNewTaskCreated}
      />
    </Box>
  );
};

export default TasksPage;
